// src/pages/AccountPage.jsx
// Mobile Account hub — surfaces profile, teams, creator link, settings, and admin.
// This is the destination for the Account tab on mobile, replacing the direct /settings link.
// Desktop users still use the sidebar which has all these links directly.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useTheme } from '../context/ThemeContext';

export default function AccountPage() {
  const { user, profile, isPro, isAdmin, hasTeamsPlan, teamsPlan, entryCount, signOut } = useAuth();
  const { teams, activeTeam, switchTeam } = useTeam();
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  // Plan display — mirrors Sidebar logic
  let planLabel, planColor, planBg, planBorder, planTo;
  if (isAdmin) {
    planLabel = '✦ Admin'; planColor = '#ef9f27'; planBg = 'rgba(239,159,39,0.1)'; planBorder = 'rgba(239,159,39,0.3)'; planTo = '/settings';
  } else if (hasTeamsPlan && teamsPlan === 'agency') {
    planLabel = 'Teams Agency'; planColor = '#ef9f27'; planBg = 'rgba(239,159,39,0.08)'; planBorder = 'rgba(239,159,39,0.25)'; planTo = '/settings?section=teams';
  } else if (hasTeamsPlan && teamsPlan === 'growth') {
    planLabel = 'Teams Growth'; planColor = '#1d9e75'; planBg = 'rgba(29,158,117,0.08)'; planBorder = 'rgba(29,158,117,0.25)'; planTo = '/settings?section=teams';
  } else if (hasTeamsPlan && teamsPlan === 'starter') {
    planLabel = 'Teams Starter'; planColor = '#7f77dd'; planBg = 'rgba(127,119,221,0.08)'; planBorder = 'rgba(127,119,221,0.25)'; planTo = '/settings?section=teams';
  } else if (isPro) {
    planLabel = 'Pro'; planColor = '#7f77dd'; planBg = 'rgba(127,119,221,0.08)'; planBorder = 'rgba(127,119,221,0.25)'; planTo = '/settings?section=plan';
  } else {
    planLabel = 'Free'; planColor = 'var(--dim)'; planBg = 'transparent'; planBorder = 'var(--border)'; planTo = '/premium';
  }

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/');
  };

  // ── Not logged in ─────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div style={s.page}>
        <h1 style={s.title}>Account</h1>
        <div style={{ ...s.card, textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>👤</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Sign in to your account</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>
            Save prompts, build skills, join teams, and more.
          </div>
          <Link to="/auth" style={s.btnPrimary}>Sign in / Sign up →</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>

      {/* ── Profile card ─────────────────────────────────────────────────── */}
      <div style={{ ...s.card, display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
        {/* Avatar */}
        <div style={{
          width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
          background: 'var(--accent-dim)', border: '2px solid var(--accent-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, color: 'var(--accent)',
        }}>
          {(profile?.display_name || profile?.username || user.email || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {profile?.display_name || profile?.username || 'My Account'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email}
          </div>
          {/* Plan badge */}
          <Link to={planTo} style={{ textDecoration: 'none', display: 'inline-flex', marginTop: 5 }}>
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 20,
              background: planBg, border: `0.5px solid ${planBorder}`,
              color: planColor, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: planColor, flexShrink: 0 }} />
              {planLabel}
            </span>
          </Link>
        </div>
        <Link to="/settings" style={{ ...s.btnSecondary, textDecoration: 'none', fontSize: 12, flexShrink: 0 }}>
          Edit
        </Link>
      </div>

      {/* ── Quick stats ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          ['Entries', entryCount ?? '—'],
          ['Teams', teams?.length ?? 0],
          ['Plan', planLabel],
        ].map(([label, val]) => (
          <div key={label} style={{ flex: 1, ...s.card, textAlign: 'center', padding: '10px 8px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>{val}</div>
            <div style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Teams ────────────────────────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Teams</div>

        {!hasTeamsPlan && !isAdmin ? (
          <Link to="/premium" style={{ textDecoration: 'none' }}>
            <div style={{ ...s.row, color: 'var(--accent)', borderBottom: 'none' }}>
              <span style={{ fontSize: 20 }}>🏢</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Upgrade to create teams</div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>Collaborate with your team on AI content</div>
              </div>
              <span style={s.chevron}>›</span>
            </div>
          </Link>
        ) : teams?.length === 0 ? (
          <Link to="/team/create" style={{ textDecoration: 'none' }}>
            <div style={{ ...s.row, color: 'var(--accent)', borderBottom: 'none' }}>
              <span style={{ fontSize: 20 }}>➕</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Create your first team</div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>Invite members and share content</div>
              </div>
              <span style={s.chevron}>›</span>
            </div>
          </Link>
        ) : (
          <>
            {teams.map(t => {
              const isActive = activeTeam?.id === t.id;
              const isOwnerOrAdmin = t.my_role === 'owner' || t.my_role === 'admin';
              return (
                <div key={t.id}>
                  <div
                    onClick={() => { switchTeam(t.id); navigate('/team'); }}
                    style={{ ...s.row, cursor: 'pointer', background: isActive ? 'var(--accent-dim)' : undefined, borderColor: isActive ? 'var(--accent-border)' : undefined }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: isActive ? 'var(--accent-dim)' : 'var(--surface2)',
                      border: `1.5px solid ${isActive ? 'var(--accent-border)' : 'var(--border2)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18,
                    }}>🏢</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? 'var(--accent)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--dim)' }}>
                        {t.my_role} {isActive ? '· active' : ''}
                      </div>
                    </div>
                    {isActive && (
                      <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 10, background: 'var(--accent-dim)', color: 'var(--accent)', border: '0.5px solid var(--accent-border)', fontWeight: 600, flexShrink: 0 }}>
                        active
                      </span>
                    )}
                    <span style={s.chevron}>›</span>
                  </div>
                  {/* Expanded actions for active team */}
                  {isActive && (
                    <div style={{ display: 'flex', gap: 8, padding: '8px 14px 4px', marginTop: -4 }}>
                      <button onClick={() => navigate('/team')} style={s.teamAction}>
                        📋 Dashboard
                      </button>
                      {isOwnerOrAdmin && (
                        <button onClick={() => navigate('/team/settings')} style={s.teamAction}>
                          ⚙ Settings
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Create another team */}
            <Link to="/team/create" style={{ textDecoration: 'none' }}>
              <div style={{ ...s.row, color: 'var(--accent)', borderBottom: 'none' }}>
                <span style={{ fontSize: 18 }}>➕</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Create another team</span>
                <span style={{ ...s.chevron, marginLeft: 'auto' }}>›</span>
              </div>
            </Link>
          </>
        )}
      </div>

      {/* ── Creator ──────────────────────────────────────────────────────── */}
      {(isPro || isAdmin) && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Creator</div>
          <Link to="/creator" style={{ textDecoration: 'none' }}>
            <div style={s.row}>
              <span style={{ fontSize: 20 }}>⭐</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Creator Dashboard</div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>Your listings, stats, and earnings</div>
              </div>
              <span style={s.chevron}>›</span>
            </div>
          </Link>
        </div>
      )}

      {/* ── Settings & account ───────────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Account</div>

        {/* Theme toggle */}
        <div onClick={toggleTheme} style={{ ...s.row, cursor: 'pointer' }}>
          <span style={{ fontSize: 20 }}>{isDark ? '☀️' : '🌙'}</span>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1 }}>
            {isDark ? 'Light mode' : 'Dark mode'}
          </span>
          {/* Toggle pill */}
          <div style={{
            width: 40, height: 22, borderRadius: 11, flexShrink: 0,
            background: isDark ? 'var(--accent)' : 'var(--border2)',
            position: 'relative', transition: 'background 0.2s',
          }}>
            <div style={{
              position: 'absolute', top: 3,
              left: isDark ? 21 : 3,
              width: 16, height: 16, borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
        </div>

        <Link to="/settings" style={{ textDecoration: 'none' }}>
          <div style={s.row}>
            <span style={{ fontSize: 20 }}>⚙️</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1 }}>Settings</span>
            <span style={s.chevron}>›</span>
          </div>
        </Link>

        <Link to="/premium" style={{ textDecoration: 'none' }}>
          <div style={s.row}>
            <span style={{ fontSize: 20 }}>✦</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                {isPro || hasTeamsPlan ? 'Manage plan' : 'Upgrade to Pro'}
              </div>
              <div style={{ fontSize: 11, color: planColor }}>{planLabel}</div>
            </div>
            <span style={s.chevron}>›</span>
          </div>
        </Link>

        {isAdmin && (
          <Link to="/admin" style={{ textDecoration: 'none' }}>
            <div style={s.row}>
              <span style={{ fontSize: 20 }}>🛡</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1 }}>Admin panel</span>
              <span style={s.chevron}>›</span>
            </div>
          </Link>
        )}

        <div onClick={handleSignOut} style={{ ...s.row, cursor: 'pointer', color: '#e24b4a', borderBottom: 'none' }}>
          <span style={{ fontSize: 20 }}>↩</span>
          <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </span>
        </div>
      </div>

      {/* Bottom padding for nav */}
      <div style={{ height: 8 }} />
    </div>
  );
}

const s = {
  page: {
    background: 'var(--bg)',
    padding: 'clamp(14px, 3vw, 24px)',
    fontFamily: 'var(--font)',
  },
  title: {
    fontSize: 'clamp(19px,4vw,25px)', fontWeight: 700,
    color: 'var(--text)', margin: '0 0 16px', letterSpacing: '-0.02em',
  },
  card: {
    background: 'var(--surface)',
    border: '0.5px solid var(--border)',
    borderRadius: 14,
    padding: '14px 16px',
  },
  section: {
    background: 'var(--surface)',
    border: '0.5px solid var(--border)',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: 14,
  },
  sectionLabel: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em',
    color: 'var(--dim)', fontWeight: 600,
    padding: '10px 16px 6px',
    borderBottom: '0.5px solid var(--border)',
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px',
    borderBottom: '0.5px solid var(--border)',
    background: 'transparent',
    transition: 'background 0.1s',
    WebkitTapHighlightColor: 'transparent',
    minHeight: 48,  /* ensures rows are never clipped */
  },
  chevron: {
    fontSize: 18, color: 'var(--dim)', flexShrink: 0, lineHeight: 1,
  },
  teamAction: {
    flex: 1, padding: '7px 12px', borderRadius: 8,
    background: 'var(--surface2)', border: '0.5px solid var(--border2)',
    fontFamily: 'var(--font)', fontSize: 12, color: 'var(--muted)',
    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
  btnPrimary: {
    display: 'inline-block', padding: '11px 24px',
    background: 'var(--accent)', color: '#fff',
    borderRadius: 10, textDecoration: 'none',
    fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600,
  },
  btnSecondary: {
    display: 'inline-flex', alignItems: 'center',
    padding: '6px 12px', borderRadius: 8,
    background: 'none', border: '0.5px solid var(--border2)',
    color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 13,
    cursor: 'pointer',
  },
};
